// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The backglass's other three score reels (M8-17).
//
// **One component, two games.** A hot-seat game reads its rows from the local session's
// `standings`; an online game reads them from the seats of the canonical document. They are the
// same thing on screen and the same thing to a player — four names and four numbers with one of
// them lit — so they are one component, and the difference lives in the two small functions that
// build its rows. Two scoreboards would be two places to get the active row wrong, and the active
// row is the only part anybody looks at while a ball is in play.
//
// **Absent only when it says nothing.** A solo game draws no scoreboard at all: one row saying
// what the big number above it already says is furniture, and 08 §4.2 is explicit that the
// machine's own DMD is the primary readout and the HUD is the minimum beside it.
//
// **The active row is marked three ways** — a class, an `aria-current`, and the word "playing" in
// the row's accessible text. That is the same rule the navigation tabs follow: a colour is never
// the only thing carrying a state, because a colour is not a sentence and about one player in
// twelve cannot read the one this would use.

import { translateOr } from '../i18n'
import { useLocalisation } from '../i18n/useLanguage'

/** One line. Built by `standingsRows` or `seatRows`, never assembled by hand at a call site. */
export interface ScoreboardRow {
  /** Stable across redraws: a seat number, which never renumbers mid-game (M8-14). */
  readonly seat: number
  readonly label: string
  /**
   * Set when the label is this product's own words rather than a person's name.
   *
   * An online seat carries a `displayName` somebody chose, and translating that would be renaming
   * them. A hot-seat row carries "Player 2", which is a sentence this product wrote and should say
   * in the reader's language.
   */
  readonly labelKey?: 'scoreboard.player'
  readonly score: number
  /** Whose ball it is. Exactly one row at most. */
  readonly active: boolean
  /**
   * A short state after the name, as a **key** rather than a word — `scoreboard.tilted`,
   * `scoreboard.left`.
   *
   * A key because the row builders below are pure functions: they cannot hold a translator, they
   * are called from a render, and they are unit-tested without a DOM. So they name the state and
   * the component says it, which also keeps the test asserting a state rather than a sentence.
   *
   * Read out as part of the row rather than shown as an icon, because both of the things it says
   * are ones a player needs when they are *not* looking at the screen: somebody who tilted wants
   * to know why their ball is over, and somebody whose opponent left wants to know why the
   * machine skipped them.
   */
  readonly note?: string
}

export interface ScoreboardProps {
  readonly rows: readonly ScoreboardRow[]
  /**
   * What this scoreboard is for, e.g. "Ball 2 of 3". Shown small above the rows.
   *
   * `undefined` is explicit in the type rather than merely optional, because the caller computes
   * it from a game that may be in attract — and under `exactOptionalPropertyTypes` "absent" and
   * "present and undefined" are different things a conditional expression cannot choose between.
   */
  readonly caption?: string | undefined
}

export function Scoreboard({ rows, caption }: ScoreboardProps): React.JSX.Element | null {
  const { t, language, locale } = useLocalisation()
  // Nothing to say for a solo game. See the note at the top: one row is furniture.
  if (rows.length < 2) return null
  return (
    <div className="stage__board" role="group" aria-label={t('nav.scores')}>
      {caption === undefined ? null : <p className="stage__board-caption">{caption}</p>}
      <ol className="stage__board-rows">
        {rows.map((row) => (
          <li
            key={row.seat}
            className={row.active ? 'stage__board-row stage__board-row--up' : 'stage__board-row'}
            {...(row.active ? { 'aria-current': 'true' as const } : {})}
          >
            <span className="stage__board-name">
              {row.labelKey === undefined
                ? row.label
                : t('scoreboard.player', { seat: row.seat })}
              {row.note === undefined ? null : (
                <span className="stage__board-note"> · {translateOr(row.note, language, row.note)}</span>
              )}
              {row.active ? <span className="sr-only"> · {t('scoreboard.playing')}</span> : null}
            </span>
            <span className="stage__board-score">{row.score.toLocaleString(locale)}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/**
 * What a player is called when nobody has told us their name.
 *
 * Kept as a function returning English rather than deleted, because it is the fallback the
 * scoreboard falls back *to* and a test asserts it. The rendered label comes from
 * `scoreboard.player` — see `ScoreboardRow.labelKey`.
 */
export function playerLabel(player: number): string {
  return `Player ${player}`
}

// ---------------------------------------------------------------------------
// The two row builders
// ---------------------------------------------------------------------------
//
// Here rather than in the stage, because the interesting part of each is the same question — which
// row is lit — and answering it twice in a component that renders both is how the two come to
// disagree. A hot-seat game's active row is the core's `currentPlayer`; an online game's is the
// seat the server says holds the turn. Neither is derivable from the other, and both are one line.

/** The core's phases in which somebody is holding the flippers, or about to be. */
const PLAYING = new Set(['ballInPlay', 'bonus', 'betweenBalls'])

/** A hot-seat game's rows, from the local session (M8-17). */
export function standingsRows(readout: {
  readonly standings: readonly {
    readonly player: number
    readonly score: number
    readonly tilted: boolean
  }[]
  readonly player: number
  readonly phase: string
}): readonly ScoreboardRow[] {
  return readout.standings.map((row) => ({
    seat: row.player,
    label: playerLabel(row.player),
    // Nobody named this seat, so the scoreboard names it — in the reader's language.
    labelKey: 'scoreboard.player' as const,
    score: row.score,
    // **Nobody is up in attract or after the last ball.** `currentPlayer` is still whoever played
    // last, and lighting their row on a finished game reads as "still their turn". `betweenBalls`
    // and `bonus` count as up, because the player has not changed yet and a row that went dark
    // during a bonus count would flicker once a ball.
    active: PLAYING.has(readout.phase) && row.player === readout.player,
    ...(row.tilted ? { note: 'scoreboard.tilted' } : {}),
  }))
}

/** An online game's rows, from the canonical document (M8-14). */
export function seatRows(game: {
  readonly seats: readonly {
    readonly seat: number
    readonly displayName: string
    readonly reportedScore: number
    readonly present: boolean
  }[]
  readonly turn: number | null
  readonly phase: string
}): readonly ScoreboardRow[] {
  return game.seats.map((seat) => ({
    seat: seat.seat,
    label: seat.displayName,
    score: seat.reportedScore,
    active: game.phase === 'playing' && seat.seat === game.turn,
    // A seat that left keeps its score and stops being dealt balls (M8-14). Saying so is what
    // stops the other three wondering why the machine skipped somebody.
    ...(seat.present ? {} : { note: 'scoreboard.left' }),
  }))
}
